import { eventTracker } from 'app/modules/Segment';
import {
  FeatureEnum,
  GroupIdEnum,
  OwnerEnum,
  TContext,
  TProperties,
} from 'app/modules/Segment/types';

const EVENTS = {
  JUS_GPT_CHAT_STARTED: 'jus_gpt_chat_started',
  JUS_GPT_CHAT_MESSAGE_SENT: 'jus_gpt_chat_message_sent',
  JUS_GPT_QUICK_CHAT_CLICKED: 'jus_gpt_quick_chat_clicked',
  JUS_GPT_FILE_UPLOADED: 'jus_gpt_file_uploaded',
  JUS_GPT_DOCX_FILE_REQUESTED: 'jus_gpt_docx_file_requested',
  JUS_GPT_DOCX_FILE_RECEIVED: 'jus_gpt_docx_file_received',
  JUS_GPT_TEXT_COPIED: 'jus_gpt_text_copied',
  JUS_GPT_CHAT_DELETED: 'jus_gpt_chat_deleted',
  JUS_GPT_CHAT_SEARCH_PERFORMED: 'jus_gpt_chat_search_performed',
  JUS_GPT_CHAT_SELECTED_FROM_SEARCH: 'jus_gpt_chat_selected_from_search',
  JUS_GPT_WHATSAPP_SUPPORT_OPENED: 'jus_gpt_whatsapp_support_opened',
  JUS_GPT_WHATSAPP_BUTTON_CLICKED: 'jus_gpt_whatsapp_button_clicked',
  JUS_GPT_FILE_UPLOAD_ERROR: 'jus_gpt_file_upload_error',
  JUS_GPT_CHAT_LOADED: 'jus_gpt_chat_loaded',
  JUS_GPT_SIDEBAR_TOGGLED: 'jus_gpt_sidebar_toggled',
  JUS_GPT_SHOW_MORE_TOGGLED: 'jus_gpt_show_more_toggled',
  JUS_GPT_DRAWER_LAUNCHED: 'jus_gpt_drawer_launched',
  JUS_GPT_DRAWER_REDIRECT: 'jus_gpt_drawer_redirect',
  JUS_GPT_DRAWER_CONVERSATION_CHANGED: 'jus_gpt_drawer_conversation_changed',
  JUS_GPT_RESPONSE_ERROR: 'jus_gpt_response_error',
  JUS_GPT_HELP_CENTER_ACTION: 'jus_gpt_help_center_action',
  JUS_GPT_DRAWER_QUICK_ACTION_CLICKED: 'jus_gpt_drawer_quick_action_clicked',
  JUS_GPT_FILE_DOWNLOADED: 'jus_gpt_file_downloaded',
  JUS_GPT_IMAGE_PREVIEWED: 'jus_gpt_image_previewed',
  JUS_GPT_BUSINESS_BANNER_CLICKED: 'jus_gpt_business_banner_clicked',
  JUS_GPT_LIMIT_WARNING_SHOWN: 'jus_gpt_limit_warning_shown',
  JUS_GPT_LIMIT_REACHED: 'jus_gpt_limit_reached',
  JUS_GPT_UPGRADE_MODAL_OPENED: 'jus_gpt_upgrade_modal_opened',
  JUS_GPT_UPGRADE_COMPLETED: 'jus_gpt_upgrade_completed',
  JUS_GPT_LIMIT_DISMISSED: 'jus_gpt_limit_dismissed',
  JUS_GPT_STUDENT_FAKE_DOOR: 'jus_gpt_student_fake_door',
  JUS_GPT_RESPONSE_FEEDBACK_CHANGED: 'jus_gpt_response_feedback_changed',
  JUS_GPT_RESPONSE_FEEDBACK_REASON_SENT: 'jus_gpt_response_feedback_reason_sent',
  JUS_GPT_PLANNING_CHAT_STARTED: 'jus_gpt_planning_chat_started',
  JUS_GPT_PLANNING_CARD_SHOWN: 'jus_gpt_planning_card_shown',
  JUS_GPT_PLANNING_CALCULATION_OPENED: 'jus_gpt_planning_calculation_opened',
  JUS_GPT_CNIS_UPLOAD_STARTED: 'jus_gpt_cnis_upload_started',
  JUS_GPT_CALCULATION_CHAT_STARTED: 'jus_gpt_calculation_chat_started',
  JUS_GPT_CALCULATION_CARD_SHOWN: 'jus_gpt_calculation_card_shown',
  JUS_GPT_CALCULATION_OPENED: 'jus_gpt_calculation_opened',
  JUS_GPT_CALCULATION_PDF_OPENED: 'jus_gpt_calculation_pdf_opened',
} as const;

export type TJusGPTResponseRating = 'LIKE' | 'DISLIKE';

export type TJusGPTUpgradeModalOrigin = 'banner' | 'badge' | 'auto';

const JUSGPT_LIBRARY_NAME = 'jusgpt-library';
const JUSGPT_FEATURE = FeatureEnum.JUSGPT;

function buildBaseProperties(
  eventText: string,
  relevantUserActivity: boolean,
  eventMeaning: string,
  overrides: Partial<TProperties> = {},
): TProperties {
  return {
    owner: OwnerEnum.INOVACAO,
    feature: JUSGPT_FEATURE,
    event_meaning: eventMeaning,
    relevant_user_activity: relevantUserActivity,
    event_text: eventText,
    ...overrides,
  } as TProperties;
}

function buildContext(componentName: string): TContext {
  return {
    groupId: GroupIdEnum.JUSGPT_GROUP,
    library: {
      name: JUSGPT_LIBRARY_NAME,
      componente: componentName,
    },
  };
}

export function trackJusGPTChatStarted(props: {
  chatId?: string;
  isQuickChat?: boolean;
  quickChatType?: string | null;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_CHAT_STARTED,
    properties: buildBaseProperties(
      'JusGPT Chat Started',
      true,
      'Disparado quando uma nova conversa do JusGPT é criada. Propriedades: `chat_id`, `is_quick_chat`, `quick_chat_type`, `timestamp`.',
      {
        chat_id: props.chatId,
        is_quick_chat: props.isQuickChat || false,
        quick_chat_type: props.quickChatType ?? null,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('chat-component'),
  });
}

export function trackJusGPTChatMessageSent(props: {
  chatId?: string;
  hasFile?: boolean;
  fileType?: string | null;
  messageLength?: number;
  isQuickChat?: boolean;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_CHAT_MESSAGE_SENT,
    properties: buildBaseProperties(
      'JusGPT Chat Message Sent',
      true,
      'Disparado quando uma mensagem é enviada no chat JusGPT. Propriedades: `chat_id`, `has_file`, `file_type`, `message_length`, `is_quick_chat`, `timestamp`.',
      {
        chat_id: props.chatId,
        has_file: props.hasFile || false,
        file_type: props.fileType ?? null,
        message_length: props.messageLength || 0,
        is_quick_chat: props.isQuickChat || false,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('chat-component'),
  });
}

export function trackJusGPTQuickChatClicked(props: {
  quickChatTitle?: string;
  quickChatPrompt?: string;
  requiresFile?: boolean;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_QUICK_CHAT_CLICKED,
    properties: buildBaseProperties(
      'JusGPT Quick Chat Clicked',
      true,
      'Disparado quando o usuário clica em um QuickChat do JusGPT. Propriedades: `quick_chat_title`, `quick_chat_prompt`, `requires_file`, `timestamp`.',
      {
        quick_chat_title: props.quickChatTitle,
        quick_chat_prompt: props.quickChatPrompt,
        requires_file: props.requiresFile || false,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('card-component'),
  });
}

// Fake door do aluno: mede interesse sem enviar nada. Nunca carrega o texto
// digitado, só o tamanho.
export function trackJusGPTStudentFakeDoor(props: {
  origem: 'mensagem' | 'quick_chat';
  quickChatTitle?: string;
  messageLength?: number;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_STUDENT_FAKE_DOOR,
    properties: buildBaseProperties(
      'JusGPT Student Fake Door',
      true,
      'Disparado quando um aluno (`is_student`) tenta enviar mensagem ou clicar num QuickChat da IA e vê o modal "Em breve". Nada é enviado à IA. Propriedades: `origem`, `quick_chat_title`, `message_length`, `timestamp`.',
      {
        origem: props.origem,
        quick_chat_title: props.quickChatTitle ?? null,
        message_length: props.messageLength ?? null,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('student-fake-door'),
  });
}


export function trackJusGPTFileUploaded(props: {
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  uploadContext?: string;
  uploadSuccess?: boolean;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_FILE_UPLOADED,
    properties: buildBaseProperties(
      'JusGPT File Uploaded',
      true,
      'Disparado quando um arquivo é enviado no JusGPT. Propriedades: `file_name`, `file_type`, `file_size`, `upload_context`, `upload_success`, `timestamp`.',
      {
        file_name: props.fileName,
        file_type: props.fileType,
        file_size: props.fileSize,
        upload_context: props.uploadContext || 'manual',
        upload_success: props.uploadSuccess ?? true,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('chat-input'),
  });
}

export function trackJusGPTDocxFileRequested(props: {
  chatId?: string;
  textLength?: number;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_DOCX_FILE_REQUESTED,
    properties: buildBaseProperties(
      'JusGPT Docx File Requested',
      true,
      'Disparado quando um arquivo DOCX é solicitado no JusGPT. Propriedades: `chat_id`, `text_length`, `timestamp`.',
      {
        chat_id: props.chatId,
        text_length: props.textLength || 0,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('message-list'),
  });
}

export function trackJusGPTDocxFileReceived(props: {
  chatId?: string;
  downloadSuccess?: boolean;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_DOCX_FILE_RECEIVED,
    properties: buildBaseProperties(
      'JusGPT Docx File Received',
      true,
      'Disparado quando um arquivo DOCX é recebido/baixado no JusGPT. Propriedades: `chat_id`, `download_success`, `timestamp`.',
      {
        chat_id: props.chatId,
        download_success: props.downloadSuccess ?? true,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('message-list'),
  });
}

export function trackJusGPTTextCopied(props: {
  chatId?: string;
  textLength?: number;
  copyMethod?: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_TEXT_COPIED,
    properties: buildBaseProperties(
      'JusGPT Text Copied',
      false,
      'Disparado quando texto é copiado para área de transferência no JusGPT. Propriedades: `chat_id`, `text_length`, `copy_method`, `timestamp`.',
      {
        chat_id: props.chatId,
        text_length: props.textLength || 0,
        copy_method: props.copyMethod || 'clipboard_api',
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('message-list'),
  });
}

export function trackJusGPTChatDeleted(props: {
  chatId?: string;
  deleteSuccess?: boolean;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_CHAT_DELETED,
    properties: buildBaseProperties(
      'JusGPT Chat Deleted',
      true,
      'Disparado quando uma conversa do JusGPT é excluída. Propriedades: `chat_id`, `delete_success`, `timestamp`.',
      {
        chat_id: props.chatId,
        delete_success: props.deleteSuccess ?? true,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('chat-sidebar'),
  });
}

export function trackJusGPTChatSearchPerformed(props: {
  searchQuery?: string;
  resultsCount?: number;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_CHAT_SEARCH_PERFORMED,
    properties: buildBaseProperties(
      'JusGPT Chat Search Performed',
      false,
      'Disparado quando a busca de conversas do JusGPT é utilizada. Propriedades: `search_query`, `results_count`, `timestamp`.',
      {
        search_query: props.searchQuery,
        results_count: props.resultsCount || 0,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('chat-sidebar'),
  });
}

export function trackJusGPTChatSelectedFromSearch(props: {
  chatId?: string;
  searchQuery?: string;
  resultPosition?: number;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_CHAT_SELECTED_FROM_SEARCH,
    properties: buildBaseProperties(
      'JusGPT Chat Selected From Search',
      true,
      'Disparado quando uma conversa do JusGPT é selecionada via busca. Propriedades: `chat_id`, `search_query`, `result_position`, `timestamp`.',
      {
        chat_id: props.chatId,
        search_query: props.searchQuery,
        result_position: props.resultPosition || 0,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('chat-sidebar'),
  });
}

export function trackJusGPTWhatsAppSupportOpened(): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_WHATSAPP_SUPPORT_OPENED,
    properties: buildBaseProperties(
      'JusGPT WhatsApp Support Opened',
      false,
      'Disparado quando o WhatsApp é aberto para suporte no JusGPT. Propriedades: `timestamp`.',
      {
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('whatsapp-button'),
  });
}

export function trackJusGPTWhatsAppButtonClicked(props: {
  phoneNumber?: string;
  contactType?: string;
  buttonLabel?: string;
  chatId?: string | null;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_WHATSAPP_BUTTON_CLICKED,
    properties: buildBaseProperties(
      'JusGPT WhatsApp Button Clicked',
      true,
      'Disparado quando um botão do WhatsApp é clicado no JusGPT. Propriedades: `phone_number`, `contact_type`, `button_label`, `chat_id`, `timestamp`.',
      {
        phone_number: props.phoneNumber,
        contact_type: props.contactType || 'generic',
        button_label: props.buttonLabel,
        chat_id: props.chatId ?? null,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('whatsapp-button'),
  });
}

export function trackJusGPTFileUploadError(props: {
  errorType?: string;
  fileName?: string;
  fileType?: string;
  fileSize?: number;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_FILE_UPLOAD_ERROR,
    properties: buildBaseProperties(
      'JusGPT File Upload Error',
      true,
      'Disparado quando há erro no upload de arquivo no JusGPT. Propriedades: `error_type`, `file_name`, `file_type`, `file_size`, `timestamp`.',
      {
        error_type: props.errorType,
        file_name: props.fileName,
        file_type: props.fileType,
        file_size: props.fileSize,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('chat-input'),
  });
}

export function trackJusGPTChatLoaded(props: {
  chatId?: string;
  messagesCount?: number;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_CHAT_LOADED,
    properties: buildBaseProperties(
      'JusGPT Chat Loaded',
      true,
      'Disparado quando uma conversa do JusGPT é carregada. Propriedades: `chat_id`, `messages_count`, `timestamp`.',
      {
        chat_id: props.chatId,
        messages_count: props.messagesCount || 0,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('chat-component'),
  });
}

export function trackJusGPTSidebarToggled(props: {
  isOpen?: boolean;
  deviceType?: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_SIDEBAR_TOGGLED,
    properties: buildBaseProperties(
      'JusGPT Sidebar Toggled',
      false,
      'Disparado quando a sidebar do JusGPT é expandida ou minimizada. Propriedades: `is_open`, `device_type`, `timestamp`.',
      {
        is_open: props.isOpen,
        device_type: props.deviceType || 'desktop',
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('chat-sidebar'),
  });
}

export function trackJusGPTShowMoreToggled(props: {
  section?: string;
  expanded?: boolean;
  itemsCount?: number;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_SHOW_MORE_TOGGLED,
    properties: buildBaseProperties(
      'JusGPT Show More Toggled',
      false,
      'Disparado quando o botão "Ver mais" ou "Ver menos" é clicado no JusGPT. Propriedades: `section`, `expanded`, `items_count`, `timestamp`.',
      {
        section: props.section,
        expanded: props.expanded,
        items_count: props.itemsCount || 0,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('chat-sidebar'),
  });
}

export function trackJusGPTDrawerLaunched(props: {
  tag?: string;
  context?: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_DRAWER_LAUNCHED,
    properties: buildBaseProperties(
      'JusGPT Drawer Launched',
      true,
      'Disparado quando o drawer do JusGPT é inicializado. Propriedades: `tag`, `context`, `timestamp`.',
      {
        tag: props.tag,
        context: props.context,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('drawer-component'),
  });
}

export function trackJusGPTDrawerRedirect(props: { tag?: string }): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_DRAWER_REDIRECT,
    properties: buildBaseProperties(
      'JusGPT Drawer Redirect',
      true,
      'Disparado quando o usuário é redirecionado a partir do drawer do JusGPT. Propriedades: `tag`, `timestamp`.',
      {
        tag: props.tag,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('drawer-component'),
  });
}

export function trackJusGPTDrawerConversationChanged(props: {
  tag?: string;
  chatId?: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_DRAWER_CONVERSATION_CHANGED,
    properties: buildBaseProperties(
      'JusGPT Drawer Conversation Changed',
      true,
      'Disparado quando a conversa selecionada no drawer do JusGPT é alterada. Propriedades: `tag`, `chat_id`, `timestamp`.',
      {
        tag: props.tag,
        chat_id: props.chatId,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('drawer-component'),
  });
}

export function trackJusGPTResponseError(props: {
  chatId?: string;
  errorType?: string;
  waitTime?: number;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_RESPONSE_ERROR,
    properties: buildBaseProperties(
      'JusGPT Response Error',
      true,
      'Disparado quando há erro/timeout na resposta do JusGPT. Propriedades: `chat_id`, `error_type`, `wait_time`, `timestamp`.',
      {
        chat_id: props.chatId,
        error_type: props.errorType || 'timeout',
        wait_time: props.waitTime || 0,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('chat-component'),
  });
}

export function trackJusGPTHelpCenterAction(props: {
  action?: string;
  source?: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_HELP_CENTER_ACTION,
    properties: buildBaseProperties(
      'JusGPT Help Center Action',
      true,
      'Disparado para ações da Central de Ajuda do JusGPT. Propriedades: `action`, `source`, `timestamp`.',
      {
        action: props.action,
        source: props.source || 'help_center_modal',
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('help-center'),
  });
}

export function trackJusGPTDrawerQuickActionClicked(props: {
  actionLabel?: string;
  actionPrompt?: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_DRAWER_QUICK_ACTION_CLICKED,
    properties: buildBaseProperties(
      'JusGPT Drawer Quick Action Clicked',
      true,
      'Disparado quando uma Quick Action é clicada no drawer de ajuda do JusGPT. Propriedades: `action_label`, `action_prompt`, `timestamp`.',
      {
        action_label: props.actionLabel,
        action_prompt: props.actionPrompt,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('drawer-component'),
  });
}

export function trackJusGPTFileDownloaded(props: {
  chatId?: string;
  fileName?: string;
  fileType?: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_FILE_DOWNLOADED,
    properties: buildBaseProperties(
      'JusGPT File Downloaded',
      true,
      'Disparado quando um arquivo é baixado pelo usuário no JusGPT. Propriedades: `chat_id`, `file_name`, `file_type`, `timestamp`.',
      {
        chat_id: props.chatId,
        file_name: props.fileName,
        file_type: props.fileType,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('message-list'),
  });
}

export function trackJusGPTImagePreviewed(props: {
  chatId?: string;
  fileName?: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_IMAGE_PREVIEWED,
    properties: buildBaseProperties(
      'JusGPT Image Previewed',
      false,
      'Disparado quando o usuário abre o preview de uma imagem no JusGPT. Propriedades: `chat_id`, `file_name`, `timestamp`.',
      {
        chat_id: props.chatId,
        file_name: props.fileName,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('message-list'),
  });
}

export function trackJusGPTBusinessBannerClicked(): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_BUSINESS_BANNER_CLICKED,
    properties: buildBaseProperties(
      'JusGPT Business Banner Clicked',
      true,
      'Disparado quando o usuário clica no banner de upgrade para o JusGPT Business. Propriedades: `timestamp`.',
      {
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('business-banner'),
  });
}

// === Voto na resposta (JUS2-122) ===
// Só dispara depois que o serviço gravou. Nunca carrega o texto do comentário,
// só se ele existe e o tamanho.

export function trackJusGPTResponseFeedbackChanged(props: {
  chatId?: string | null;
  messageId: string;
  rating: TJusGPTResponseRating | null;
  previousRating: TJusGPTResponseRating | null;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_RESPONSE_FEEDBACK_CHANGED,
    properties: buildBaseProperties(
      'JusGPT Response Feedback Changed',
      true,
      'Disparado quando o usuário dá, troca ou desfaz 👍/👎 numa resposta do JusGPT servida pelo ai-platform-service. `rating` null indica voto desfeito. Propriedades: `chat_id`, `message_id`, `rating`, `previous_rating`, `timestamp`.',
      {
        chat_id: props.chatId ?? null,
        message_id: props.messageId,
        rating: props.rating,
        previous_rating: props.previousRating,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('message-feedback'),
  });
}

export function trackJusGPTResponseFeedbackReasonSent(props: {
  chatId?: string | null;
  messageId: string;
  motivo?: string | null;
  commentLength?: number;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_RESPONSE_FEEDBACK_REASON_SENT,
    properties: buildBaseProperties(
      'JusGPT Response Feedback Reason Sent',
      true,
      'Disparado quando o usuário envia motivo e/ou comentário do 👎 numa resposta do JusGPT. Nunca carrega o texto do comentário. Propriedades: `chat_id`, `message_id`, `motivo`, `has_comment`, `comment_length`, `timestamp`.',
      {
        chat_id: props.chatId ?? null,
        message_id: props.messageId,
        motivo: props.motivo ?? null,
        has_comment: (props.commentLength ?? 0) > 0,
        comment_length: props.commentLength ?? 0,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('message-feedback'),
  });
}

// === Funil de limite de mensagens ===
// Nenhum destes eventos dispara para usuário ilimitado (inclui limite virtualmente
// ilimitado) nem para B2B — os call sites garantem isso.

export function trackJusGPTLimitWarningShown(props: {
  plan?: string;
  used?: number | null;
  limit?: number | null;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_LIMIT_WARNING_SHOWN,
    properties: buildBaseProperties(
      'JusGPT Limit Warning Shown',
      true,
      'Disparado uma vez por sessão quando o badge de 90% do limite de mensagens fica visível. Propriedades: `plan`, `used`, `limit`, `timestamp`.',
      {
        plan: props.plan ?? '',
        used: props.used ?? null,
        limit: props.limit ?? null,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('quota-badge'),
  });
}

export function trackJusGPTLimitReached(props: {
  plan?: string;
  limit?: number | null;
  periodEnd?: string | null;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_LIMIT_REACHED,
    properties: buildBaseProperties(
      'JusGPT Limit Reached',
      true,
      'Disparado uma vez por sessão quando o usuário bate o teto de mensagens (evento WS ou load com blocked). Propriedades: `plan`, `limit`, `period_end`, `timestamp`.',
      {
        plan: props.plan ?? '',
        limit: props.limit ?? null,
        period_end: props.periodEnd ?? null,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('quota-blocked'),
  });
}

export function trackJusGPTUpgradeModalOpened(props: {
  planAtual?: string;
  origem: TJusGPTUpgradeModalOrigin;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_UPGRADE_MODAL_OPENED,
    properties: buildBaseProperties(
      'JusGPT Upgrade Modal Opened',
      true,
      'Disparado quando o modal de planos abre a partir do fluxo de limite do JusGPT. Propriedades: `plan_atual`, `origem` (banner | badge | auto), `timestamp`.',
      {
        plan_atual: props.planAtual ?? '',
        origem: props.origem,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('quota-upgrade-modal'),
  });
}

export function trackJusGPTUpgradeCompleted(props: {
  planOrigem?: string;
  planDestino?: string;
  diasAteUpgrade: number | null;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_UPGRADE_COMPLETED,
    properties: buildBaseProperties(
      'JusGPT Upgrade Completed',
      true,
      'Disparado no sucesso do checkout de upgrade iniciado pelo fluxo de limite do JusGPT. `dias_ate_upgrade` null indica conversão sem ter batido o teto (via aviso de 90%). Propriedades: `plan_origem`, `plan_destino`, `dias_ate_upgrade`, `timestamp`.',
      {
        plan_origem: props.planOrigem ?? '',
        plan_destino: props.planDestino ?? '',
        dias_ate_upgrade: props.diasAteUpgrade,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('quota-upgrade-modal'),
  });
}

// Planejamento previdenciário (spec 2026-10-20). Funil de adoção: o service mede
// "pediram" e "concluíram" pelo rastro em `message_capabilities`; o front mede a
// entrada e o "abriram o resultado". O cruzamento é por `calculation_id`.
// Nenhum evento leva CPF, nome do cliente ou valor monetário.

export type TJusGPTPlanningOrigin = 'card' | 'link' | 'banner';

export function trackJusGPTPlanningChatStarted(props: {
  chatId?: string | null;
  origem: TJusGPTPlanningOrigin;
  hasSeed: boolean;
  flagEnabled: boolean;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_PLANNING_CHAT_STARTED,
    properties: buildBaseProperties(
      'JusGPT Planning Chat Started',
      true,
      'Disparado quando o chat do planejamento previdenciário é aberto pelo card de calculadoras (`origem: card`), pelo link `?conversa=planejamento-previdenciario` (`origem: link`) ou pela faixa "Novidade" do início na experiência nova (`origem: banner`). `has_seed` indica que o calcSeed passou na validação e foi no context do chat. Propriedades: `chat_id`, `origem`, `has_seed`, `flag_enabled`, `timestamp`.',
      {
        chat_id: props.chatId ?? null,
        origem: props.origem,
        has_seed: props.hasSeed,
        flag_enabled: props.flagEnabled,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('planning-entry'),
  });
}

export function trackJusGPTPlanningCardShown(props: {
  chatId?: string | null;
  calculationId: string;
  verdict: string;
  rulesEligibleCount: number;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_PLANNING_CARD_SHOWN,
    properties: buildBaseProperties(
      'JusGPT Planning Card Shown',
      false,
      'Disparado quando o card do resultado do planejamento previdenciário (calc.planejamento) chega ao vivo na conversa, uma vez por cálculo. Não dispara no reload da conversa. Propriedades: `chat_id`, `calculation_id`, `verdict`, `rules_eligible_count`, `timestamp`.',
      {
        chat_id: props.chatId ?? null,
        calculation_id: props.calculationId,
        verdict: props.verdict,
        rules_eligible_count: props.rulesEligibleCount,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('planning-card'),
  });
}

export function trackJusGPTPlanningCalculationOpened(props: {
  chatId?: string | null;
  calculationId: string;
  verdict: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_PLANNING_CALCULATION_OPENED,
    properties: buildBaseProperties(
      'JusGPT Planning Calculation Opened',
      true,
      'Disparado quando o usuário clica em "Abrir cálculo completo" no card do planejamento previdenciário, que abre /planejamento-previdenciario/<id> em nova aba. Vale também para card recarregado. Propriedades: `chat_id`, `calculation_id`, `verdict`, `timestamp`.',
      {
        chat_id: props.chatId ?? null,
        calculation_id: props.calculationId,
        verdict: props.verdict,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('planning-card'),
  });
}

// Cálculo previdenciário (calc.previdenciaria, spec 2026-10-27). Mesmo funil do
// planejamento: o front mede a entrada, o card exibido e os cliques. Cruzamento
// com o service por `calculation_id`. Nenhum evento leva CPF, nome ou valor.

export function trackJusGPTCalculationChatStarted(props: {
  chatId?: string | null;
  origem: TJusGPTPlanningOrigin;
  hasSeed: boolean;
  flagEnabled: boolean;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_CALCULATION_CHAT_STARTED,
    properties: buildBaseProperties(
      'JusGPT Calculation Chat Started',
      true,
      'Disparado quando o chat do cálculo previdenciário é aberto pelo card de calculadoras (`origem: card`) ou pelo link `?conversa=calculo-previdenciario` (`origem: link`). `has_seed` indica que o calcSeed `{ modo: calculo }` passou na validação e foi no context do chat. Propriedades: `chat_id`, `origem`, `has_seed`, `flag_enabled`, `timestamp`.',
      {
        chat_id: props.chatId ?? null,
        origem: props.origem,
        has_seed: props.hasSeed,
        flag_enabled: props.flagEnabled,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('calculation-entry'),
  });
}

export function trackJusGPTCalculationCardShown(props: {
  chatId?: string | null;
  calculationId: string;
  granted: boolean;
  hasPdf: boolean;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_CALCULATION_CARD_SHOWN,
    properties: buildBaseProperties(
      'JusGPT Calculation Card Shown',
      false,
      'Disparado quando o card do resultado do cálculo previdenciário (calc.previdenciaria) chega ao vivo na conversa, uma vez por cálculo. Não dispara no reload. Propriedades: `chat_id`, `calculation_id`, `granted` (requisitos do benefício principal cumpridos), `has_pdf`, `timestamp`.',
      {
        chat_id: props.chatId ?? null,
        calculation_id: props.calculationId,
        granted: props.granted,
        has_pdf: props.hasPdf,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('calculation-card'),
  });
}

export function trackJusGPTCalculationOpened(props: {
  chatId?: string | null;
  calculationId: string;
  granted: boolean;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_CALCULATION_OPENED,
    properties: buildBaseProperties(
      'JusGPT Calculation Opened',
      true,
      'Disparado quando o usuário clica em "Abrir cálculo completo" no card do cálculo previdenciário, que abre /previdenciaria/<id> em nova aba. Vale também para card recarregado. Propriedades: `chat_id`, `calculation_id`, `granted`, `timestamp`.',
      {
        chat_id: props.chatId ?? null,
        calculation_id: props.calculationId,
        granted: props.granted,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('calculation-card'),
  });
}

export function trackJusGPTCalculationPdfOpened(props: {
  chatId?: string | null;
  calculationId: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_CALCULATION_PDF_OPENED,
    properties: buildBaseProperties(
      'JusGPT Calculation PDF Opened',
      true,
      'Disparado quando o usuário clica em "Abrir PDF" no card do cálculo previdenciário (só aparece quando o PDF já veio pronto no turno). Propriedades: `chat_id`, `calculation_id`, `timestamp`.',
      {
        chat_id: props.chatId ?? null,
        calculation_id: props.calculationId,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('calculation-card'),
  });
}

// Upload do CNIS no card `calc.intencao` (experiência nova). Nenhum evento leva
// nome, tamanho ou conteúdo do arquivo.

export function trackJusGPTCnisUploadStarted(props: {
  chatId?: string | null;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_CNIS_UPLOAD_STARTED,
    properties: buildBaseProperties(
      'JusGPT CNIS Upload Started',
      true,
      'Disparado quando o usuário escolhe ou solta um PDF válido na área de upload do CNIS do card calc.intencao. O arquivo vira anexo da próxima mensagem. Propriedades: `chat_id`, `timestamp`.',
      {
        chat_id: props.chatId ?? null,
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('cnis-upload'),
  });
}

export function trackJusGPTLimitDismissed(props: { plan?: string }): void {
  eventTracker.track({
    event: EVENTS.JUS_GPT_LIMIT_DISMISSED,
    properties: buildBaseProperties(
      'JusGPT Limit Dismissed',
      true,
      'Disparado quando o usuário fecha o modal de planos do fluxo de limite sem converter. Propriedades: `plan`, `timestamp`.',
      {
        plan: props.plan ?? '',
        timestamp: new Date().toISOString(),
      },
    ),
    context: buildContext('quota-upgrade-modal'),
  });
}
